import React from 'react';

import LoadingSpinner from '../layout/LoadingSpinner';

type ColumnDef = { label: string; span: number };

interface AdminTableLayoutProps {
  columns: ColumnDef[];
  headerFilters?: React.ReactNode;
  children: React.ReactNode;
  isLoading?: boolean;
  pagination?: {
    itemsPerPage: number;
    onItemsPerPageChange: (value: number) => void;
    currentPage: number;
    totalPages?: number;
    onPageChange: (page: number) => void;
  };
  itemsPerPageSelector?: React.ReactNode; // allow custom selector if needed
}

export default function AdminTableLayout({
  columns,
  headerFilters,
  children,
  isLoading,
  pagination,
  itemsPerPageSelector,
}: AdminTableLayoutProps) {
  const totalGridCols = columns.reduce((sum, c) => sum + c.span, 0);

  return (
    <div className="w-full">
      {/* Fixed Table Header with Embedded Filters — omitted when no columns provided */}
      {columns.length > 0 && (
        <div className="admin-table-header border-b">
          <div className="px-6 py-3">
            {/* First Row - Column Labels */}
            <div
              className={`grid gap-4 text-xs font-medium uppercase tracking-wider text-[hsl(var(--admin-table-header-text))] mb-3 grid-cols-${totalGridCols}`}
            >
              {columns.map((col, idx) => (
                <div key={idx} className={`col-span-${col.span}`}>{col.label}</div>
              ))}
            </div>

            {/* Second Row - Filter Controls */}
            <div className={`grid gap-4 grid-cols-${totalGridCols}`}>
              {headerFilters ?? columns.map((col, idx) => (
                <div key={idx} className={`col-span-${col.span}`}></div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Table Body with page-level scrolling */}
      <div className="divide-y divide-gray-200">
        {isLoading && (
          <div className="flex justify-center items-center py-12">
            <LoadingSpinner />
          </div>
        )}

        {children}

        {/* Pagination */}
        {pagination?.totalPages && (
          <div className="admin-table-footer px-6 py-4 border-t">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Show</span>
                {itemsPerPageSelector}
                <span className="text-sm text-gray-600">per page</span>
              </div>

              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Page</span>
                {/* Consumers can provide their own input; keep neutral to avoid coupling */}
                <input
                  type="number"
                  min={1}
                  value={pagination.currentPage}
                  max={pagination.totalPages}
                  onChange={(e) => {
                    const value = parseInt(e.currentTarget.value);
                    if (pagination.totalPages && value <= pagination.totalPages && value > 0) {
                      pagination.onPageChange(value);
                    }
                  }}
                  className="w-16 h-8 text-sm border rounded px-2"
                />
                <span className="text-sm text-gray-600">of {pagination.totalPages}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}



